import { reactive } from 'vue';
// The Taptic bridge. `native.js` imports only app-config, so this direction is
// the acyclic one — and it is the import whose absence made every shipped iOS
// app silent. See `haptic()` below.
import { nativeHaptic } from './native.js';

// Small shared helpers.
//
// `$`/`$$` survive the move to Vue on purpose. Most of the app should never
// reach for them — that is what refs and templates are for — but the chassis
// still has to talk to things Vue does not own: the document element, a
// canvas handed to a share sheet, an element inside a WebView plugin.

export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

export const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

export const uid = (n = 10) =>
  [...crypto.getRandomValues(new Uint8Array(n))]
    .map((b) => 'abcdefghijklmnopqrstuvwxyz0123456789'[b % 36])
    .join('');

export function debounce(fn, ms = 150) {
  let t;
  return (...a) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...a), ms);
  };
}

/** Minimal pub/sub. Handlers never throw into the emitter. */
export function createBus() {
  const map = new Map();
  return {
    on(evt, fn) {
      (map.get(evt) ?? map.set(evt, new Set()).get(evt)).add(fn);
      return () => map.get(evt)?.delete(fn);
    },
    emit(evt, payload) {
      for (const fn of map.get(evt) ?? []) {
        try { fn(payload); } catch (e) { console.error(`bus:${evt}`, e); }
      }
    },
  };
}

/**
 * Transient status.
 *
 * A reactive list rather than a hidden div this function reaches into, so any
 * module can raise a toast without importing a component, and core/ui/Toast.vue
 * can render them without anything importing IT. The old version needed an
 * element with a specific id to already exist in index.html, which is exactly
 * the coupling the rewrite is meant to remove.
 */
export const toasts = reactive([]);

export function dismiss(id) {
  const i = toasts.findIndex((t) => t.id === id);
  if (i !== -1) toasts.splice(i, 1);
}

export function toast(text, { ms = 2200, kind = '', onClick = null } = {}) {
  const id = uid(8);
  toasts.push({ id, text, kind, onClick });
  // ms === 0 means it stays until something dismisses it — the update prompt.
  if (ms) setTimeout(() => dismiss(id), ms);
  return id;
}

/**
 * Haptics. THE ONE FUNCTION AN APP CALLS.
 *
 * This comment used to say the native build "overrides it with the Taptic
 * Engine". Nothing overrode it. `core/native.js` had the real Capacitor bridge
 * written and correct, and no file in the tree imported it — so on iOS, where
 * `navigator.vibrate` does not exist at all, every app built on this chassis
 * shipped with no haptics whatsoever. Silently: a phone missing haptics because
 * of a chassis bug feels exactly like a phone with the user's own haptics
 * toggle turned off, so the one explanation anybody reaches for is also the one
 * that hides it. Two files asserted the bridge and neither one built it.
 *
 * Now it delegates. Native gets the Taptic Engine; the web gets
 * `navigator.vibrate` where it exists and nothing where it does not, which is
 * the correct degradation rather than a promise.
 *
 * THE KINDS, and picking the right one is most of what makes haptics read as
 * considered rather than as buzzing:
 *
 *   tick / pop / heavy               something moved or landed
 *   success / warning / error        the OUTCOME of something
 *   selection                        scrubbing discrete values — stepper, segment
 *
 * The preference is honoured HERE, once, so an app cannot get it half-right by
 * calling the native function directly. `SettingsModal.vue` wires the toggle to
 * `setHaptics`.
 */
let hapticsEnabled = true;
export function setHaptics(on) { hapticsEnabled = !!on; }

// Web fallback durations. Notification kinds get a pattern rather than a
// single buzz, because on the web that is the only way an outcome can feel
// different from a tap.
const WEB_PATTERN = {
  tick: 8,
  pop: 14,
  heavy: 22,
  undo: 6,
  selection: 5,
  'selection-start': 5,
  'selection-end': 5,
  success: [10, 40, 10],
  warning: [16, 60, 16],
  error: [24, 60, 24, 60, 24],
};

export function haptic(kind = 'tick') {
  if (!hapticsEnabled) return false;
  // Native first, and only if it actually fired. `nativeHaptic` returns false
  // off-native and when the plugin is missing, so the web path below is a real
  // fallback rather than a branch that can silently swallow the call.
  if (nativeHaptic(kind)) return true;
  try { return navigator.vibrate?.(WEB_PATTERN[kind] ?? 8) ?? false; } catch { return false; }
}

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function formatDuration(ms) {
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}
